import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { validateAndProcessTimeFrame } from '../../../../../common';
import { LIST_SERVER_PAGINATION_INITIAL_STATE } from '../../../../../common/models/list.model';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { ALLOCATION_TERMINAL_VIEW_DEFAULT_PAGE_SIZE, AllocationViewFilterName } from '../constants';
import { AllocationViewListResponse, AllocationViewUli, DriverViewList } from '../models';
import {
  AllocationDriverViewListLoad,
  AllocationViewListInitialState,
  AllocationViewListLoad,
} from './allocation-view-list.actions';
import { AllocationViewListService } from './allocation-view-list.service';

export interface AllocationViewListStateModel extends AllocationViewListResponse {
  isMoreButtonVisible: boolean;
  driverViewItems: DriverViewList;
}

const initialState: AllocationViewListStateModel = {
  ...LIST_SERVER_PAGINATION_INITIAL_STATE,
  size: ALLOCATION_TERMINAL_VIEW_DEFAULT_PAGE_SIZE,
  isMoreButtonVisible: false,
  driverViewItems: new DriverViewList(),
};

@State<AllocationViewListStateModel>({
  name: 'allocationViewList',
  defaults: initialState,
})
@Injectable()
export class AllocationViewListState {
  @Selector()
  static getState(state: AllocationViewListStateModel): AllocationViewListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: AllocationViewListStateModel): AllocationViewUli[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: AllocationViewListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isMoreButtonVisible(state: AllocationViewListStateModel): boolean {
    return state.isMoreButtonVisible;
  }

  constructor(
    private allocationViewListService: AllocationViewListService,
    private notificationService: NotificationService,
  ) {}

  @Action(AllocationViewListLoad)
  getAllocationViewList(
    ctx: StateContext<AllocationViewListStateModel>,
    { payload }: AllocationViewListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });
    payload[AllocationViewFilterName.TimeFrame] = validateAndProcessTimeFrame(
      payload[AllocationViewFilterName.TimeFrame],
    );
    return this.allocationViewListService.getAllocationViewList(payload).pipe(
      map((response: AllocationViewListResponse) => {
        const { page } = payload;
        ctx.patchState({
          items: response.items,
          pageNumber: page,
          isMoreButtonVisible: response?.pageNumber + 1 < response?.totalPages,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationDriverViewListLoad)
  getAllocationDriverViewList(
    ctx: StateContext<AllocationViewListStateModel>,
    { payload }: AllocationDriverViewListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewListService.getAllocationDriverViewList(payload).pipe(
      take(1),
      map((allocationViewUliList: DriverViewList) => {
        allocationViewUliList.calledToDockWebUliAllocationViews.forEach((e: AllocationViewUli) => {
          e.highlightRow = false;
        });
        allocationViewUliList.registeredWebUliAllocationViews.forEach((e: AllocationViewUli) => {
          e.highlightRow = false;
        });
        ctx.patchState({
          driverViewItems: allocationViewUliList,
          pageNumber: 0,
          isMoreButtonVisible: false,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewListInitialState)
  setInitialState(ctx: StateContext<AllocationViewListInitialState>): void {
    ctx.setState(initialState);
  }
}
